import { useId, useRef, useState } from 'react';
import pluralize from 'pluralize';
import {
  Box,
  Center,
  Flex,
  Group,
  Loader,
  Stack,
  Text,
  UnstyledButton,
  VisuallyHidden,
} from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router';
import TextInput from 'src/components/design-system/input/TextInput';
import Textarea from 'src/components/design-system/input/Textarea';
import { Button } from 'src/design-system/Button';
import { CheckSpeakersModal } from 'src/meetings/components/CheckSpeakersModal';
import {
  ChooseSpeakerModal,
  type SelectedSpeaker,
} from 'src/meetings/components/ChooseSpeakerModal';
import { useEvent } from 'src/meetings/hooks/useEvent';
import type { CalendarEvent, Participant } from 'src/meetings/models';
import { useTranscript } from 'src/meetings/hooks/useTranscript';
import { type AssignSpeakerBody, useAssignSpeaker } from 'src/meetings/hooks/useAssignSpeaker';
import { useConfirmSpeakers } from 'src/meetings/hooks/useConfirmSpeakers';
import { useFlags } from 'src/feature-flags/FlagsHook';
import {
  type SpeakerSuggestion,
  TranscriptionStatus,
} from 'src/meetings/detail/tabs/transcript/types';
import { TranscriptSegment } from 'src/meetings/detail/tabs/transcript/TranscriptSegment';
import { meetingDetailPath, meetingScopeFromParams, type MeetingScope } from 'src/meetings/scope';
import {
  effectiveSpeakerStatus,
  getSpeakers,
  type MeetingSpeaker,
  type SpeakerStatus,
} from 'src/meetings/utils/speaker-utils';
import { notifyAndLogError, notifyAndWarn } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

const DESCRIPTION_MAX_LENGTH = 1000;
const SUGGESTION_HINT = 'Suggested by Siesta. Check it’s right before you identify them.';

type SpeakerForm = {
  name: string;
  description: string;
  selected: SelectedSpeaker | null;
};

const EMPTY_FORM: SpeakerForm = { name: '', description: '', selected: null };

// A suggestion naming a client or colleague links their record; any other name is typed in.
function withSuggestion(form: SpeakerForm, suggestion: SpeakerSuggestion): SpeakerForm {
  const { name, clientId, userId } = suggestion;
  if (clientId) return { ...form, selected: { kind: 'client', id: clientId, name } };
  if (userId) return { ...form, selected: { kind: 'user', id: userId, name } };
  return { ...form, name, selected: null };
}

function isApplied(form: SpeakerForm, suggestion: SpeakerSuggestion): boolean {
  const { selected } = form;
  if (suggestion.clientId) {
    return selected?.kind === 'client' && selected.id === suggestion.clientId;
  }
  if (suggestion.userId) {
    return selected?.kind === 'user' && selected.id === suggestion.userId;
  }
  return selected === null && form.name === suggestion.name;
}

// Naming an external attendee already on the meeting who has a description links them as they are.
function describedAttendee(
  event: CalendarEvent | null | undefined,
  name: string,
): Participant | undefined {
  const wanted = name.trim().toLocaleLowerCase();
  if (!wanted) return undefined;
  return (event?.eventParticipants ?? [])
    .map(eventParticipant => eventParticipant.participant)
    .find(
      participant =>
        participant.participantType === 'EXTERNAL' &&
        participant.name?.trim().toLocaleLowerCase() === wanted &&
        Boolean(participant.description?.trim()),
    );
}
// Returns the label to activate after assigning `assignedLabel`: the next
// unidentified speaker in appearance order, falling back to the previous one
// when the last was assigned, or null when none remain.
function getNextSpeakerLabel(
  speakers: MeetingSpeaker[],
  assignedLabel: string,
  handled: ReadonlySet<string>,
): string | null {
  // `speakers` is derived from the transcript, which has not refetched yet, so everyone just
  // assigned is still listed as needing attention. `handled` carries the voices dealt with in
  // this visit, without which a quick run through several speakers lands back on a finished
  // one instead of finishing.
  const outstanding = (speaker: MeetingSpeaker) =>
    speaker.label !== assignedLabel && !speaker.correctionLabels.every(label => handled.has(label));
  if (!speakers.some(outstanding)) return null;
  const currentIndex = speakers.findIndex(speaker => speaker.label === assignedLabel);
  const after = speakers.slice(currentIndex + 1).find(outstanding);
  const before = [...speakers.slice(0, Math.max(currentIndex, 0))].reverse().find(outstanding);
  return (after ?? before)?.label ?? null;
}

const STATUS_LABELS: Record<SpeakerStatus, string> = {
  unidentified: 'Not identified',
  identified: 'Identified, not confirmed',
  confirmed: 'Confirmed',
};

function SpeakerStatusMarker({ status }: { status: SpeakerStatus }) {
  if (status === 'confirmed') {
    return (
      <IconCheck
        size={14}
        color="var(--mantine-color-gray-6)"
        style={{ flexShrink: 0 }}
        aria-hidden
      />
    );
  }
  // Unidentified speakers need naming (solid brand dot); identified ones are named
  // automatically but still await a human's confirmation (hollow brand ring).
  const filled = status === 'unidentified';
  return (
    <Box
      w={8}
      h={8}
      bg={filled ? 'brand.6' : 'transparent'}
      aria-hidden
      style={{
        borderRadius: '50%',
        flexShrink: 0,
        border: filled ? undefined : '1.5px solid var(--mantine-color-brand-6)',
      }}
    />
  );
}

function SpeakerTab({
  speaker,
  status,
  isActive,
  onClick,
}: {
  speaker: MeetingSpeaker;
  status: SpeakerStatus;
  isActive: boolean;
  onClick: () => void;
}) {
  const activeColour = status === 'confirmed' ? 'gray.10' : 'brand.8';
  // A filled dot, a hollow ring and a tick are the only thing separating the three states, which
  // is nothing at all to a screen reader. The status rides as a description rather than in the
  // button's name, so the tab is still announced (and still found) by the speaker's name.
  const statusId = useId();
  return (
    <>
      <UnstyledButton
        onClick={onClick}
        aria-pressed={isActive}
        aria-describedby={statusId}
        bg={isActive ? 'brand.1' : 'white'}
        py="xxs"
        px="xs"
        style={{ borderRadius: 6 }}
      >
        <Flex align="center" gap="xxs">
          <SpeakerStatusMarker status={status} />
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c={isActive ? activeColour : 'gray.6'}>
            {speaker.displayName}
          </Text>
        </Flex>
      </UnstyledButton>
      <VisuallyHidden id={statusId}>{STATUS_LABELS[status]}</VisuallyHidden>
    </>
  );
}

export default function AssignSpeakerPage() {
  const {
    familyId,
    clientId,
    eventId = '',
    speakerLabel = '',
  } = useParams<{
    familyId?: string;
    clientId?: string;
    eventId: string;
    speakerLabel: string;
  }>();
  const navigate = useNavigate();
  const scope: MeetingScope = meetingScopeFromParams({ familyId, clientId });

  const { event } = useEvent(eventId);
  const { transcript, isLoading } = useTranscript({ eventId });
  const assignSpeaker = useAssignSpeaker(scope, eventId);
  const confirmSpeakers = useConfirmSpeakers(scope, eventId);
  const { flagEnabledSpeakerConfirmation, flagEnabledSpeakerPipelineRefactor } = useFlags();

  const meetingTitle = event?.title;
  const pageTitle = meetingTitle ? `Speakers for ${meetingTitle}` : 'Speakers';

  const [activeLabel, setActiveLabel] = useState(speakerLabel);
  // A display name is not a stable handle on a speaker: assigning one rewrites it, and the
  // backend's own casing (`format_name`) need not match what we predicted. The diarisation
  // label survives every relabel, so once we have acted on a speaker we follow them by voice.
  const [activeVoice, setActiveVoice] = useState<string | null>(null);
  // Voices assigned during this visit. A ref, because the next-speaker decision is made in the
  // same handler that records one, before any re-render.
  const handledVoices = useRef<Set<string>>(new Set());
  const [formByLabel, setFormByLabel] = useState<Record<string, SpeakerForm>>({});
  const [editingLabels, setEditingLabels] = useState<Record<string, boolean>>({});
  const [chooseClientOpen, setChooseClientOpen] = useState(false);
  const [showCheckModal, setShowCheckModal] = useState(false);

  const meetingPath = meetingDetailPath(scope, eventId);

  if (isLoading && !transcript) {
    return (
      <Center py="xxl">
        <Loader />
      </Center>
    );
  }

  if (transcript?.status !== TranscriptionStatus.READY) {
    return (
      <Box pt={36} px="xxl" pb="xxl">
        <Text c="gray.6">No transcript is available for this meeting.</Text>
      </Box>
    );
  }

  const speakers = getSpeakers(transcript.segments, transcript.speakerStatuses);

  if (speakers.length === 0) {
    return (
      <Box pt={36} px="xxl" pb="xxl">
        <Flex align="center" justify="space-between" gap="sm">
          <Text size="displaySm" fw={FONT_WEIGHTS.semibold} c="gray.10">
            {pageTitle}
          </Text>
          <Button variant="text" onClick={() => navigate(meetingPath)}>
            Back to meeting
          </Button>
        </Flex>
        <Text mt="lg" size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6">
          No speakers were detected in this meeting.
        </Text>
      </Box>
    );
  }

  const effectiveStatus = (speaker: MeetingSpeaker): SpeakerStatus =>
    effectiveSpeakerStatus(speaker.status, flagEnabledSpeakerConfirmation);

  const reviewSpeakers = speakers.filter(speaker => effectiveStatus(speaker) !== 'confirmed');
  const toReviewCount = reviewSpeakers.length;
  const speakerWord = pluralize('speaker', speakers.length);
  const reviewVerb = flagEnabledSpeakerConfirmation ? 'review' : 'identify';
  const subtitle =
    toReviewCount > 0
      ? `${speakers.length} ${speakerWord} · ${toReviewCount} to ${reviewVerb}`
      : `${speakers.length} ${speakerWord} · all confirmed`;

  const activeSpeaker =
    (activeVoice !== null
      ? speakers.find(speaker => speaker.correctionLabels.includes(activeVoice))
      : undefined) ??
    speakers.find(speaker => speaker.label === activeLabel) ??
    reviewSpeakers[0] ??
    speakers[0];
  const currentLabel = activeSpeaker.label;
  // Suggestions are keyed by diarisation label and one speaker here can cover
  // several, so take the first label Siesta has a suggestion for. Only an
  // unidentified speaker is pre-filled; reassigning starts from a blank form.
  const suggestion =
    activeSpeaker.status === 'unidentified'
      ? activeSpeaker.correctionLabels
          .map(label => transcript.speakerSuggestions?.[label])
          .find(entry => entry !== undefined)
      : undefined;
  const initialForm = suggestion ? withSuggestion(EMPTY_FORM, suggestion) : EMPTY_FORM;
  const form = formByLabel[currentLabel] ?? initialForm;

  const updateForm = (patch: Partial<SpeakerForm>) =>
    setFormByLabel(previous => ({
      ...previous,
      [currentLabel]: { ...(previous[currentLabel] ?? initialForm), ...patch },
    }));

  const speakerSegments =
    transcript.segments?.filter(segment => segment.speaker === currentLabel) ?? [];

  const activeStatus = effectiveStatus(activeSpeaker);
  const isEditing = editingLabels[currentLabel] ?? false;
  // "Change" edits an already-named speaker (identified or confirmed) via the same form.
  const isReassigning = activeStatus !== 'unidentified' && isEditing;
  const showForm = activeStatus === 'unidentified' || isReassigning;

  const canReuseAttendee = flagEnabledSpeakerPipelineRefactor && form.selected === null;
  const attendee = canReuseAttendee ? describedAttendee(event, form.name) : undefined;
  const canAssign = form.selected
    ? true
    : form.name.trim().length > 0 && (attendee !== undefined || form.description.trim().length > 0);

  const isShowingSuggestion = suggestion !== undefined && isApplied(form, suggestion);

  const selectedSpeakerName = form.selected?.name ?? null;

  const startReassigning = () =>
    setEditingLabels(previous => ({ ...previous, [currentLabel]: true }));

  const stopReassigning = () => {
    setEditingLabels(previous => ({ ...previous, [currentLabel]: false }));
    setFormByLabel(previous => {
      const next = { ...previous };
      delete next[currentLabel];
      return next;
    });
  };

  function buildParticipant(): AssignSpeakerBody['participant'] {
    const selected = form.selected;
    if (selected?.kind === 'client') return { participantType: 'CLIENT', clientId: selected.id };
    if (selected?.kind === 'user') return { participantType: 'USER', userId: selected.id };
    if (selected?.kind === 'keyContact')
      return {
        participantType: 'EXTERNAL',
        name: selected.name,
        description: selected.description,
      };
    return {
      participantType: 'EXTERNAL',
      name: form.name.trim(),
      description: attendee?.description?.trim() ?? form.description.trim(),
    };
  }

  async function handleAssign() {
    // One speaker on this screen can cover several diarisation labels (e.g. two
    // unknown speakers identified as the same person), so correct every one.
    const correctionLabels = activeSpeaker.correctionLabels;
    const wasReassigning = isReassigning;
    const participant = buildParticipant();
    const predictedLabel = form.selected?.name ?? form.name.trim();
    try {
      await assignSpeaker.mutateAsync({ speakerLabels: correctionLabels, participant });
    } catch (error) {
      notifyAndLogError(
        'Failed to identify speaker',
        'Could not identify this speaker. Please try again.',
        error,
      );
      return;
    }

    // Reassigning an already-named speaker updates them in place. Keep the tab on that
    // speaker by their voice — the name shown against them is about to change.
    if (wasReassigning) {
      stopReassigning();
      // The voice is the reliable handle, but on transcripts predating diarisation capture it
      // is only the old display name, which the relabel is about to invalidate. Keep the
      // predicted name too so those transcripts still land on the right tab when it matches.
      setActiveVoice(correctionLabels[0] ?? null);
      setActiveLabel(predictedLabel);
      return;
    }

    advanceOrFinish(correctionLabels);
  }

  // After confirming/identifying a speaker, move to the next one still needing review, or
  // finish once none remain. `handled` are the voices just dealt with: the transcript has not
  // refetched, so they still look outstanding in `reviewSpeakers`.
  function advanceOrFinish(handled: string[]) {
    for (const label of handled) handledVoices.current.add(label);

    const nextLabel = getNextSpeakerLabel(reviewSpeakers, currentLabel, handledVoices.current);
    if (nextLabel !== null) {
      setActiveVoice(null);
      setActiveLabel(nextLabel);
      return;
    }
    navigate(meetingPath);
  }

  // Confirming an auto-named speaker agrees with the name already there. The backend knows
  // who the voice is attributed to, so nothing is renamed and nobody is matched by name
  // here. It confirms nothing for a voice with no record behind it (a name only heard in
  // the meeting), and says so, which is when we have to ask who they are.
  async function handleConfirmIdentified() {
    const speakerLabels = activeSpeaker.correctionLabels;
    let unconfirmed: string[] = [];
    try {
      const response = await confirmSpeakers.mutateAsync({ speakerLabels });
      unconfirmed = response.data.unconfirmed;
    } catch (error) {
      notifyAndLogError(
        'Failed to confirm speaker',
        'Could not confirm this speaker. Please try again.',
        error,
      );
      return;
    }

    // Nothing to agree with: the name came from the meeting rather than from a record, so the
    // adviser has to say who this is. Say why, or the form appears for no visible reason.
    if (unconfirmed.length === speakerLabels.length) {
      notifyAndWarn(
        'Tell us who this is',
        `We don't have a record behind ${activeSpeaker.displayName}, so we can't confirm the name on its own.`,
      );
      startReassigning();
      return;
    }

    // Part of the speaker confirmed. They stay under review, so keep the adviser here rather
    // than advancing past a speaker still carrying an unnamed voice.
    if (unconfirmed.length > 0) {
      notifyAndWarn(
        'Some statements still need a name',
        `We confirmed ${activeSpeaker.displayName}, but not every voice under that name. Change them to say who the rest are.`,
      );
      return;
    }

    advanceOrFinish(speakerLabels);
  }

  function handleBackToMeeting() {
    if (toReviewCount > 0) {
      setShowCheckModal(true);
    } else {
      navigate(meetingPath);
    }
  }

  return (
    <>
      <Box pt={36} px="xxl" pb="xxl">
        <Flex align="center" justify="space-between" gap="sm">
          <Stack gap={4}>
            <Text size="displaySm" fw={FONT_WEIGHTS.semibold} c="gray.10">
              {pageTitle}
            </Text>
            <Text size="textSm" c="gray.6">
              {subtitle}
            </Text>
          </Stack>
          <Button variant="text" onClick={handleBackToMeeting}>
            Back to meeting
          </Button>
        </Flex>

        {speakers.length > 1 && (
          <Flex mt="lg" gap="xxxs" wrap="wrap">
            {speakers.map(speaker => (
              <SpeakerTab
                key={speaker.label}
                speaker={speaker}
                status={effectiveStatus(speaker)}
                isActive={speaker.label === currentLabel}
                onClick={() => {
                  setActiveVoice(null);
                  setActiveLabel(speaker.label);
                }}
              />
            ))}
          </Flex>
        )}

        <Stack
          mt="lg"
          gap="md"
          p="lg"
          style={{ border: '1px solid var(--mantine-color-gray-1)', borderRadius: 20 }}
        >
          {showForm && form.selected && (
            <>
              <Stack gap={4}>
                <Text size="textLg" fw={FONT_WEIGHTS.semibold} c="gray.10">
                  Is this {selectedSpeakerName}?
                </Text>
                <Text size="textSm" c="gray.6">
                  {isShowingSuggestion
                    ? SUGGESTION_HINT
                    : 'Check this is the right person before you confirm them.'}
                </Text>
              </Stack>

              <Flex justify="flex-end" align="center" gap="sm">
                {isReassigning && (
                  <Button variant="text" size="sm" onClick={stopReassigning}>
                    Keep {activeSpeaker.displayName}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => updateForm({ selected: null })}
                >
                  Change
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={assignSpeaker.isPending}
                  loading={assignSpeaker.isPending}
                  onClick={handleAssign}
                >
                  Confirm {selectedSpeakerName}
                </Button>
              </Flex>
            </>
          )}

          {showForm && !form.selected && (
            <>
              <Flex align="flex-start" justify="space-between" gap="md">
                <Stack gap={4}>
                  <Text size="textLg" fw={FONT_WEIGHTS.semibold} c="gray.10">
                    {isReassigning
                      ? 'Who is this speaker?'
                      : `Who is ${activeSpeaker.displayName}?`}
                  </Text>
                  <Text size="textSm" c="gray.6">
                    Link them to a client, or add their name and who they are.
                  </Text>
                </Stack>
                <Button variant="secondary" size="sm" onClick={() => setChooseClientOpen(true)}>
                  Choose a speaker
                </Button>
              </Flex>

              <TextInput
                label="Name"
                size="md"
                placeholder="Enter speaker name"
                required
                value={form.name}
                onChange={event => updateForm({ name: event.currentTarget.value })}
                description={isShowingSuggestion ? SUGGESTION_HINT : undefined}
                inputWrapperOrder={['label', 'input', 'description', 'error']}
                styles={{
                  input: { border: '1px solid var(--mantine-color-gray-3)' },
                }}
              />

              {attendee ? (
                <Text size="textSm" c="gray.6">
                  Already on this meeting: {attendee.description}
                </Text>
              ) : (
                <Textarea
                  label="Who they are"
                  size="md"
                  placeholder={'e.g. "David\'s wife" or "solicitor from Thornton & Co"'}
                  required
                  autosize
                  minRows={2}
                  maxLength={DESCRIPTION_MAX_LENGTH}
                  value={form.description}
                  onChange={event => updateForm({ description: event.currentTarget.value })}
                  description={`${form.description.length}/${DESCRIPTION_MAX_LENGTH}`}
                  inputWrapperOrder={['label', 'input', 'description', 'error']}
                  styles={{
                    input: { border: '1px solid var(--mantine-color-gray-3)' },
                    description: { textAlign: 'right' },
                  }}
                />
              )}

              {suggestion && !isShowingSuggestion && (
                <Group>
                  <Button
                    variant="text"
                    size="sm"
                    onClick={() => updateForm(withSuggestion(form, suggestion))}
                  >
                    Use Siesta’s suggestion: {suggestion.name}
                  </Button>
                </Group>
              )}

              <Flex justify="flex-end" align="center" gap="sm">
                {isReassigning && (
                  <Button variant="text" size="sm" onClick={stopReassigning}>
                    Keep {activeSpeaker.displayName}
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!canAssign || assignSpeaker.isPending}
                  loading={assignSpeaker.isPending}
                  onClick={handleAssign}
                >
                  {isReassigning ? 'Save speaker' : 'Identify speaker'}
                </Button>
              </Flex>
            </>
          )}

          {!showForm && (
            <Stack gap="sm">
              <Flex align="center" justify="space-between" gap="sm">
                <Flex align="center" gap="sm">
                  <Text size="textLg" fw={FONT_WEIGHTS.semibold} c="gray.10">
                    {activeSpeaker.displayName}
                  </Text>
                  <Flex
                    align="center"
                    gap="xxxs"
                    py={2}
                    px="xs"
                    bg={activeStatus === 'identified' ? 'brand.0' : 'gray.0'}
                    style={{ borderRadius: 20 }}
                  >
                    <SpeakerStatusMarker status={activeStatus} />
                    <Text
                      size="textSm"
                      fw={FONT_WEIGHTS.medium}
                      c={activeStatus === 'identified' ? 'brand.7' : 'gray.7'}
                    >
                      {activeStatus === 'identified' ? 'Identified' : 'Confirmed'}
                    </Text>
                  </Flex>
                </Flex>
                <Flex align="center" gap="sm">
                  <Button variant="secondary" size="sm" onClick={startReassigning}>
                    Change
                  </Button>
                  {activeStatus === 'identified' && (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={confirmSpeakers.isPending}
                      loading={confirmSpeakers.isPending}
                      onClick={handleConfirmIdentified}
                    >
                      Confirm {activeSpeaker.displayName}
                    </Button>
                  )}
                </Flex>
              </Flex>
              {activeStatus === 'identified' && (
                <Text size="textSm" c="gray.6">
                  Siesta identified this speaker automatically. Confirm it&apos;s right, or change
                  them.
                </Text>
              )}
            </Stack>
          )}
        </Stack>

        <Box mt="xl">
          <Group gap="xs" mb="sm" align="baseline">
            <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
              What they said
            </Text>
            <Text size="textSm" c="gray.6">
              {pluralize('statement', activeSpeaker.statementCount, true)}
            </Text>
          </Group>

          <Stack gap="xxs">
            {speakerSegments.map((segment, index) => (
              <TranscriptSegment
                key={`${segment.startTime}-${index}`}
                segment={segment}
                selected={false}
                hideSpeaker
              />
            ))}
          </Stack>
        </Box>
      </Box>

      {event && (
        <ChooseSpeakerModal
          opened={chooseClientOpen}
          onClose={() => setChooseClientOpen(false)}
          scope={scope}
          event={event}
          onSelect={selected => updateForm({ selected })}
        />
      )}

      <CheckSpeakersModal
        opened={showCheckModal}
        count={toReviewCount}
        countMeans={flagEnabledSpeakerConfirmation ? 'unreviewed' : 'unidentified'}
        onClose={() => setShowCheckModal(false)}
        onContinue={() => navigate(meetingPath)}
      />
    </>
  );
}
